import { type WorkerPendingVisitInvitesResponse } from "@clipboard-health/contract-home-health-api";
import { isNil } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { isCbhApiAxiosError } from "@src/appV2/Errors/errorUtils";
import { useToast } from "@src/appV2/lib";
import { formatCentsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { useBookVisit } from "@src/appV2/redesign/InHome/BookingBottomSheet/useBookVisit";
import {
  readableVisitTypeMapping,
  shortenedDaysOfWeekMapping,
  VisitPricingTypes,
} from "@src/appV2/redesign/InHome/constants";
import { Card, CbhIcon, Text } from "@src/appV2/redesign/system/ui-components";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { isEmpty } from "lodash";
import pluralize from "pluralize";
import { useState } from "react";

import { LoadingButton } from "../../components/LoadingButton";
import { ShiftCardDivider } from "../../Shift/Card/CardDivider";
import { BookingCardContentWrapper } from "../../Shift/CardContentWrapper";
import { DotSeparatedText } from "../../Shift/WorkerShift/DotSeparatedText";

interface VisitInviteCardProps {
  invite: WorkerPendingVisitInvitesResponse["data"][0];
}

function formatPayRate(
  payRateInMinorUnits: number | null | undefined,
  pricingType: string
): string {
  if (isNil(payRateInMinorUnits)) {
    return "";
  }

  const suffix = pricingType === VisitPricingTypes.PER_HOUR ? "/hr" : "/visit";
  return `${formatCentsAsUsd(payRateInMinorUnits)}${suffix}`;
}

function formatDaysOfWeek(daysOfWeek: string[]): string {
  if (isEmpty(daysOfWeek)) {
    return "";
  }

  return daysOfWeek.map((day) => shortenedDaysOfWeekMapping[day] ?? day).join("/");
}

export function VisitInviteCard(props: VisitInviteCardProps) {
  const { invite } = props;
  const { visit, visitId } = invite.attributes;
  const { workerReq, type, pricingType, payRateInMinorUnits, daysOfWeek, estimatedDuration } =
    visit;
  const { workplaceName } = visit.case;
  const { showSuccessToast } = useToast();

  const worker = useDefinedWorker();
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  const { mutateAsync: bookVisit, isLoading: isBooking } = useBookVisit(visitId, {
    onError: (error) => {
      if (isCbhApiAxiosError(error)) {
        setErrorMessage(error.response.data.errors[0]?.detail ?? "Failed to accept visit invite");
      } else {
        setErrorMessage("Failed to accept visit invite");
      }
    },
  });

  const handleAccept = async () => {
    setErrorMessage(undefined);
    await bookVisit({
      data: {
        type: "visit",
        attributes: { bookedWorkerId: worker.userId },
      },
    });
    showSuccessToast("Invite Accepted. See Bookings for more details");
  };

  const visitTypeLabel = readableVisitTypeMapping[type] ?? type;
  const payLabel = formatPayRate(payRateInMinorUnits, pricingType);
  const daysLabel = formatDaysOfWeek(daysOfWeek);
  const durationLabel = isNil(estimatedDuration)
    ? ""
    : `${estimatedDuration} ${pluralize("hr", estimatedDuration)}`;

  const detailItems = [visitTypeLabel, workerReq].filter(Boolean);

  return (
    <Card outlined>
      <BookingCardContentWrapper sx={{ gap: 1 }}>
        <Stack spacing={5} direction="row" alignItems="flex-start">
          <Box
            sx={(theme) => ({
              display: "inline-flex",
              alignItems: "center",
              padding: 7,
              borderRadius: theme.borderRadius?.small,
              flexShrink: 0,
              background: theme.palette.homeHealth?.visit.gradient,
            })}
          >
            <CbhIcon type="house-filled" />
          </Box>

          <Stack spacing={2} sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              spacing={3}
            >
              <Text semibold variant="h6" sx={{ flexShrink: 1 }}>
                {workplaceName}
              </Text>
              {payLabel && (
                <Text semibold variant="h6" sx={{ flexShrink: 0 }}>
                  {payLabel}
                </Text>
              )}
            </Stack>
            <DotSeparatedText>
              {detailItems.map((item) => (
                <Text key={item} color={(theme) => theme.palette.text.secondary} variant="body1">
                  {item}
                </Text>
              ))}
            </DotSeparatedText>

            {(daysLabel || durationLabel) && (
              <DotSeparatedText>
                {daysLabel && (
                  <Text color={(theme) => theme.palette.text.secondary} variant="body1">
                    {daysLabel}
                  </Text>
                )}
                {durationLabel && (
                  <Text color={(theme) => theme.palette.text.secondary} variant="body1">
                    {durationLabel}
                  </Text>
                )}
              </DotSeparatedText>
            )}
          </Stack>
        </Stack>

        {errorMessage && (
          <Text color={(theme) => theme.palette.intent?.error.text} variant="body2">
            {errorMessage}
          </Text>
        )}

        <ShiftCardDivider />

        <LoadingButton
          fullWidth
          isLoading={isBooking}
          variant="contained"
          startIconType="checkmark"
          onClick={handleAccept}
        >
          Accept
        </LoadingButton>
      </BookingCardContentWrapper>
    </Card>
  );
}
